import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toastTop } from './toast-place';
import { CoinIcon } from './coin-text';
import { Medal } from './Medal';
import { rungMedal, useMilestoneLabels } from './milestone-labels';
/** How long a toast stays on screen, slide-in included (ms). */
export const MILESTONE_TOAST_MS = 3200;
/**
 * Module 132 (WP-132.6, approved mockup E3 in Aqua) — a tier or ★ step reached
 * during a move: a ribbon slides in from the right edge just under the status
 * capsule, after the move (the plugin queues it to the move's end), at most one
 * per move with simultaneous rungs merged ("2 milestones reached!"). Aqua, not
 * gold: gold means coins.
 *
 * It takes no taps (the board under it stays playable) and never shares the
 * screen with a popup: one due while a popup is up waits for it to close.
 */
export function MilestoneToast({ toast, blocked, onShown }) {
    const { t, nf, name, tierLabel } = useMilestoneLabels();
    const seen = useRef(toast?.seq ?? 0);
    const [shown, setShown] = useState(null);
    const [top, setTop] = useState(null);
    const ref = useRef(null);
    useEffect(() => {
        if (blocked) {
            setShown(null);
            return undefined;
        }
        if (!toast || toast.seq === seen.current)
            return undefined;
        seen.current = toast.seq;
        setShown(toast);
        return undefined;
    }, [toast, blocked]);
    const shownRef = useRef(onShown);
    shownRef.current = onShown;
    useEffect(() => {
        if (!shown)
            return undefined;
        shownRef.current?.(shown.levels.length);
        const id = window.setTimeout(() => setShown(null), MILESTONE_TOAST_MS);
        return () => window.clearTimeout(id);
    }, [shown]);
    // Under the status capsule (portrait) or the Top Bar (landscape): toast-place.ts.
    useLayoutEffect(() => {
        if (!shown)
            return;
        const el = ref.current;
        if (!el)
            return;
        // Under the status capsule in portrait, under the Top Bar in landscape (toast-place.ts).
        const top = toastTop(el);
        if (top !== null)
            setTop(top);
    }, [shown]);
    if (!shown || shown.levels.length === 0)
        return null;
    const levels = shown.levels;
    const one = levels.length === 1 ? levels[0] : null;
    const coins = levels.reduce((sum, l) => sum + l.coins, 0);
    return (_jsxs("div", { ref: ref, className: "bj-ms-toast", style: top === null ? { visibility: 'hidden' } : { top }, role: "status", "data-testid": "bj-milestone-toast", "data-count": levels.length, children: [_jsx("span", { className: "bj-ms-toast__medals", "aria-hidden": "true", children: levels.slice(0, 2).map((l) => {
                    const m = rungMedal(l.index);
                    return _jsx(Medal, { tier: m.tier, stars: m.stars, milestone: l.id, size: 34 }, l.key);
                }) }), _jsxs("span", { className: "bj-ms-toast__text", children: [_jsx("b", { children: one
                            ? `${name(one.id)} — ${tierLabel(one.index)}`
                            : t('blockjam.milestones.toastMany', { n: levels.length }) }), coins > 0 && (_jsxs("small", { children: [t('blockjam.milestones.claimIn'), " +", nf(coins), _jsx(CoinIcon, {})] }))] })] }, shown.seq));
}
